// Role registry — the single source of truth for the 5 roles + their nav.

export const ROLES = {
  anonymous: {
    key: "anonymous",
    label: "Browse as guest",
    short: "Guest",
    blurb: "Read-only access to the public marketplace, parcels, architects, and resources.",
  },
  owner_builder: {
    key: "owner_builder",
    label: "Owner / Builder",
    short: "Owner",
    blurb: "Homeowners, CDCs, and developers building quality housing in their own neighborhoods.",
  },
  architect: {
    key: "architect",
    label: "Architect",
    short: "Architect",
    blurb: "Licensed architects publishing pre-reviewed designs and consulting on projects.",
  },
  city_staff: {
    key: "city_staff",
    label: "City Staff",
    short: "City",
    blurb: "Municipal oversight, design review, and code change tracking.",
  },
  platform_admin: {
    key: "platform_admin",
    label: "Platform Admin",
    short: "Admin",
    blurb: "Operates the WHOLE Neighborhoods platform end-to-end.",
  },
};

export const ROLE_ORDER = [
  "anonymous",
  "owner_builder",
  "architect",
  "city_staff",
  "platform_admin",
];

// Public areas are visible to everyone.
const PUBLIC_AREAS = [
  { to: "/marketplace", label: "Marketplace" },
  { to: "/parcels",     label: "Parcels" },
  { to: "/architects",  label: "Architects" },
  { to: "/resources",   label: "Resources" },
];

// Role-aware nav — each role sees only what is relevant.
// For architects, this is the "approved" nav. Unapproved architect users get a
// reduced nav (see navForUser below).
export const NAV_BY_ROLE = {
  anonymous: PUBLIC_AREAS,

  owner_builder: [
    { to: "/owner",                label: "Dashboard",     end: true, icon: "layout-grid" },
    { to: "/owner/saved",          label: "Saved",         icon: "bookmark" },
    { to: "/owner/messages",       label: "Messages",      icon: "message-square" },
    { to: "/owner/projects",       label: "Projects",      icon: "folder" },
    { to: "/owner/payments",       label: "Payments",      icon: "credit-card" },
    { to: "/owner/notifications",  label: "Notifications", icon: "bell" },
    { divider: true },
    { to: "/marketplace",          label: "Marketplace",   icon: "compass" },
    { to: "/resources",            label: "Resources",     icon: "book-open" },
    { divider: true },
    { to: "/owner/profile",        label: "Profile",       icon: "user" },
  ],

  architect: [
    { to: "/architect",              label: "Dashboard",     end: true, icon: "layout-grid" },
    { to: "/architect/inquiries",    label: "Inquiries",     icon: "inbox" },
    { to: "/architect/designs",      label: "Designs",       icon: "file-text" },
    { to: "/architect/projects",     label: "Projects",      icon: "folder" },
    { to: "/architect/payments",     label: "Receivables",   icon: "credit-card" },
    { to: "/architect/notifications",label: "Notifications", icon: "bell" },
    { divider: true },
    { to: "/resources",              label: "Resources",     icon: "book-open" },
    { divider: true },
    { to: "/architect/profile",      label: "Firm Profile",  icon: "award" },
    { to: "/architect/account",      label: "Account",       icon: "user" },
  ],

  city_staff: [
    { to: "/city",                label: "Dashboard",     end: true, icon: "layout-grid" },
    { to: "/city/heatmap",        label: "Heatmap",       icon: "map" },
    { to: "/city/reports",        label: "Reports",       icon: "bar-chart-3" },
    { to: "/city/reviews",        label: "Reviews",       icon: "clipboard-check" },
    { to: "/city/cases",          label: "Accela Cases",  icon: "briefcase" },
    { to: "/city/audit",          label: "Audit",         icon: "list" },
    { to: "/city/notifications",  label: "Notifications", icon: "bell" },
    { divider: true },
    { to: "/resources",           label: "Resources",     icon: "book-open" },
    { divider: true },
    { to: "/city/account",        label: "Account",       icon: "user" },
  ],

  platform_admin: [
    { to: "/admin",                label: "Dashboard",     end: true, icon: "layout-grid" },
    { to: "/admin/applications",   label: "Applications",  icon: "inbox" },
    { to: "/admin/users",          label: "Users",         icon: "users" },
    { to: "/admin/architects",     label: "Architects",    icon: "award" },
    { to: "/admin/code-changes",   label: "Code Changes",  icon: "file-text" },
    { to: "/admin/payments",       label: "Revenue",       icon: "credit-card" },
    { to: "/admin/reports",        label: "Reports",       icon: "bar-chart-3" },
    { to: "/admin/audit",          label: "Audit",         icon: "list" },
    { to: "/admin/notifications",  label: "Notifications", icon: "bell" },
    { divider: true },
    { to: "/resources",            label: "Resources",     icon: "book-open" },
    { divider: true },
    { to: "/admin/settings",       label: "Settings",      icon: "settings" },
    { to: "/admin/account",        label: "Account",       icon: "user" },
  ],
};

/**
 * Resolves the nav for a given (role, user). Unapproved architects (those whose
 * account_status is not "active") see only the Application link until approval,
 * enforced server-side too.
 */
export function navForUser(role, user) {
  if (role === "architect" && user && user.account_status !== "active") {
    return [{ to: "/architect/application", label: "Application" }];
  }
  return NAV_BY_ROLE[role] || NAV_BY_ROLE.anonymous;
}
